// src/userDataSync.js
// Utility for syncing user data between localStorage and Firebase Realtime Database

import { ref, set, get, onValue, off } from "firebase/database";
import { database } from "./firebaseConfig";

// ==================== PROFILE SYNC ====================

/**
 * Sync profile data to Firebase
 * @param {string} userId - Firebase auth user ID
 * @param {object} profileData - { displayName, avatarColor, soundEnabled, darkMode }
 */
export async function syncProfileToFirebase(userId, profileData) {
  if (!userId) return;

  try {
    const profileRef = ref(database, `users/${userId}/profile`);
    const dataToSync = {
      ...profileData,
      updatedAt: Date.now(),
    };

    await set(profileRef, dataToSync);
    console.log("✅ Profile synced to Firebase");
    return true;
  } catch (error) {
    console.error("❌ Error syncing profile to Firebase:", error);
    return false;
  }
}

/**
 * Load profile data from Firebase
 * @param {string} userId - Firebase auth user ID
 * @returns {object|null} Profile data or null
 */
export async function loadProfileFromFirebase(userId) {
  if (!userId) return null;

  try {
    const profileRef = ref(database, `users/${userId}/profile`);
    const snapshot = await get(profileRef);

    if (snapshot.exists()) {
      console.log("✅ Profile loaded from Firebase");
      return snapshot.val();
    } else {
      console.log("ℹ️ No profile data in Firebase");
      return null;
    }
  } catch (error) {
    console.error("❌ Error loading profile from Firebase:", error);
    return null;
  }
}

/**
 * Listen to profile changes in real-time
 * @param {string} userId - Firebase auth user ID
 * @param {function} callback - Function to call when data changes
 * @returns {function} Unsubscribe function
 */
export function listenToProfileChanges(userId, callback) {
  if (!userId) return () => {};

  const profileRef = ref(database, `users/${userId}/profile`);

  const listener = onValue(profileRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    }
  });

  // Return unsubscribe function
  return () => off(profileRef, "value", listener);
}

// ==================== TOUR PROGRESS SYNC ====================

/**
 * Sync tour progress to Firebase
 * @param {string} userId - Firebase auth user ID
 * @param {string} tourId - Tour identifier (e.g., "san-juan")
 * @param {object} progressData - { currentStep, answers, finished, etc. }
 */
export async function syncTourProgressToFirebase(userId, tourId, progressData) {
  if (!userId || !tourId) return;

  try {
    const progressRef = ref(database, `users/${userId}/tourProgress/${tourId}`);
    const dataToSync = {
      ...progressData,
      lastUpdated: Date.now(),
    };

    await set(progressRef, dataToSync);
    console.log(`✅ Tour progress synced for ${tourId}`);
    return true;
  } catch (error) {
    console.error(`❌ Error syncing tour progress for ${tourId}:`, error);
    return false;
  }
}

/**
 * Load tour progress from Firebase
 * @param {string} userId - Firebase auth user ID
 * @param {string} tourId - Tour identifier
 * @returns {object|null} Progress data or null
 */
export async function loadTourProgressFromFirebase(userId, tourId) {
  if (!userId || !tourId) return null;

  try {
    const progressRef = ref(database, `users/${userId}/tourProgress/${tourId}`);
    const snapshot = await get(progressRef);

    if (snapshot.exists()) {
      console.log(`✅ Tour progress loaded for ${tourId}`);
      return snapshot.val();
    } else {
      console.log(`ℹ️ No tour progress in Firebase for ${tourId}`);
      return null;
    }
  } catch (error) {
    console.error(`❌ Error loading tour progress for ${tourId}:`, error);
    return null;
  }
}

/**
 * Load all tour progress for a user
 * @param {string} userId - Firebase auth user ID
 * @returns {object} All tour progress data
 */
export async function loadAllTourProgressFromFirebase(userId) {
  if (!userId) return {};

  try {
    const progressRef = ref(database, `users/${userId}/tourProgress`);
    const snapshot = await get(progressRef);

    if (snapshot.exists()) {
      console.log("✅ All tour progress loaded from Firebase");
      return snapshot.val();
    } else {
      console.log("ℹ️ No tour progress data in Firebase");
      return {};
    }
  } catch (error) {
    console.error("❌ Error loading all tour progress:", error);
    return {};
  }
}

/**
 * Listen to tour progress changes in real-time
 * @param {string} userId - Firebase auth user ID
 * @param {string} tourId - Tour identifier
 * @param {function} callback - Function to call when data changes
 * @returns {function} Unsubscribe function
 */
export function listenToTourProgressChanges(userId, tourId, callback) {
  if (!userId || !tourId) return () => {};

  const progressRef = ref(database, `users/${userId}/tourProgress/${tourId}`);

  const listener = onValue(progressRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    }
  });

  return () => off(progressRef, "value", listener);
}

// ==================== BADGES SYNC ====================

/**
 * Sync badge data to Firebase
 * @param {string} userId - Firebase auth user ID
 * @param {string} badgeId - Badge identifier (tour ID)
 * @param {object} badgeData - { earned, earnedAt, distance }
 */
export async function syncBadgeToFirebase(userId, badgeId, badgeData) {
  if (!userId || !badgeId) return;

  try {
    const badgeRef = ref(database, `users/${userId}/badges/${badgeId}`);
    await set(badgeRef, badgeData);
    console.log(`✅ Badge synced for ${badgeId}`);
    return true;
  } catch (error) {
    console.error(`❌ Error syncing badge for ${badgeId}:`, error);
    return false;
  }
}

/**
 * Load all badges from Firebase
 * @param {string} userId - Firebase auth user ID
 * @returns {object} All badge data
 */
export async function loadBadgesFromFirebase(userId) {
  if (!userId) return {};

  try {
    const badgesRef = ref(database, `users/${userId}/badges`);
    const snapshot = await get(badgesRef);

    if (snapshot.exists()) {
      console.log("✅ Badges loaded from Firebase");
      return snapshot.val();
    } else {
      console.log("ℹ️ No badge data in Firebase");
      return {};
    }
  } catch (error) {
    console.error("❌ Error loading badges:", error);
    return {};
  }
}

// ==================== MIGRATION UTILITIES ====================

/**
 * Migrate localStorage data to Firebase (one-time migration)
 * @param {string} userId - Firebase auth user ID
 * @param {string} userEmail - User's email for localStorage keys
 */
export async function migrateLocalStorageToFirebase(userId, userEmail) {
  if (!userId || !userEmail) {
    console.log("⚠️ Cannot migrate: missing userId or userEmail");
    return;
  }

  // Check if already migrated
  const migrationKey = `migrated:${userEmail}`;
  if (localStorage.getItem(migrationKey) === "true") {
    console.log("ℹ️ Data already migrated");
    return;
  }

  try {
    console.log("🔄 Starting migration from localStorage to Firebase...");

    // Migrate profile
    const profileKey = `profile:${userEmail}`;
    const profileData = localStorage.getItem(profileKey);
    if (profileData) {
      const profile = JSON.parse(profileData);
      await syncProfileToFirebase(userId, profile);
      console.log("✅ Profile migrated");
    }

    // Migrate tour progress (scan for all tour progress keys)
    const tourIds = ["san-juan", "key-west", "chicago", "mexico-city"]; // Add more as needed
    for (const tourId of tourIds) {
      const progressKey = `${tourId}:progress`;
      const progressData = localStorage.getItem(progressKey);
      if (progressData) {
        const progress = JSON.parse(progressData);
        await syncTourProgressToFirebase(userId, tourId, progress);
        console.log(`✅ Tour progress migrated for ${tourId}`);
      }
    }

    // Mark as migrated
    localStorage.setItem(migrationKey, "true");
    console.log("✅ Migration complete!");

  } catch (error) {
    console.error("❌ Migration failed:", error);
  }
}

// ==================== HYBRID SYNC UTILITIES ====================

/**
 * Save profile with hybrid approach (localStorage + Firebase)
 * @param {string} userId - Firebase auth user ID
 * @param {string} userEmail - User's email for localStorage key
 * @param {object} profileData - Profile data to save
 */
export async function saveProfileHybrid(userId, userEmail, profileData) {
  // Save to localStorage immediately (instant UI update)
  localStorage.setItem(`profile:${userEmail}`, JSON.stringify(profileData));

  // Save to Firebase in background
  if (userId) {
    await syncProfileToFirebase(userId, profileData);
  }
}

/**
 * Load profile with hybrid approach (Firebase first, fallback to localStorage)
 * @param {string} userId - Firebase auth user ID
 * @param {string} userEmail - User's email for localStorage key
 * @returns {object|null} Profile data
 */
export async function loadProfileHybrid(userId, userEmail) {
  // Try Firebase first
  if (userId) {
    const firebaseData = await loadProfileFromFirebase(userId);
    if (firebaseData) {
      // Also update localStorage for offline access
      localStorage.setItem(`profile:${userEmail}`, JSON.stringify(firebaseData));
      return firebaseData;
    }
  }

  // Fallback to localStorage
  const localData = localStorage.getItem(`profile:${userEmail}`);
  if (localData) {
    return JSON.parse(localData);
  }

  return null;
}

/**
 * Save tour progress with hybrid approach
 * @param {string} userId - Firebase auth user ID
 * @param {string} tourId - Tour identifier
 * @param {object} progressData - Progress data to save
 */
export async function saveTourProgressHybrid(userId, tourId, progressData) {
  // Save to localStorage immediately
  localStorage.setItem(`${tourId}:progress`, JSON.stringify(progressData));

  // Save to Firebase in background
  if (userId) {
    await syncTourProgressToFirebase(userId, tourId, progressData);
  }
}

/**
 * Load tour progress with hybrid approach
 * @param {string} userId - Firebase auth user ID
 * @param {string} tourId - Tour identifier
 * @returns {object|null} Progress data
 */
export async function loadTourProgressHybrid(userId, tourId) {
  // Try Firebase first
  if (userId) {
    const firebaseData = await loadTourProgressFromFirebase(userId, tourId);
    if (firebaseData) {
      // Also update localStorage for offline access
      localStorage.setItem(`${tourId}:progress`, JSON.stringify(firebaseData));
      return firebaseData;
    }
  }

  // Fallback to localStorage
  const localData = localStorage.getItem(`${tourId}:progress`);
  if (localData) {
    return JSON.parse(localData);
  }

  return null;
}

// ==================== CONFLICT RESOLUTION ====================

/**
 * Resolve data conflicts using last-write-wins with timestamps
 * @param {object} localData - Data from localStorage
 * @param {object} firebaseData - Data from Firebase
 * @returns {object} The most recent data
 */
export function resolveConflict(localData, firebaseData) {
  if (!localData) return firebaseData;
  if (!firebaseData) return localData;

  const localTimestamp = localData.updatedAt || localData.lastUpdated || 0;
  const firebaseTimestamp = firebaseData.updatedAt || firebaseData.lastUpdated || 0;

  if (firebaseTimestamp > localTimestamp) {
    console.log("🔄 Using Firebase data (more recent)");
    return firebaseData;
  } else {
    console.log("🔄 Using local data (more recent)");
    return localData;
  }
}
